import { useCallback, useEffect, useRef, useState } from "react";

type DelayedHoverProps<E> = {
  /** The delay in milliseconds before the hover state is considered active. */
  delayMs?: number;
  /** The callback to execute when the hover state becomes active after the delay. */
  onDelayedHoverStart?(event: E): void;
  /** The callback to execute when the hover state ends after being active. */
  onDelayedHoverEnd?(event: E): void;
};

type DelayedHoverReturn<E> = {
  /** Whether the element is currently being hovered. */
  isHovering: boolean;
  /** The callback to call when the hover starts. */
  onHoverStart(event: E): void;
  /** The callback to call when the hover ends. */
  onHoverEnd(event: E): void;
};

/**
 * Only apply a hover effect after a delay, to avoid flickering when quickly moving the mouse over many objects.
 *
 * @returns The callbacks to pass to the hoverable element.
 */
export function useDelayedHover<E = PointerEvent>({
  delayMs = 100,
  onDelayedHoverStart,
  onDelayedHoverEnd,
}: DelayedHoverProps<E>): DelayedHoverReturn<E> {
  const [isHovering, setIsHovering] = useState(false);

  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onHoverStart = useCallback(
    (event: E) => {
      if (hoverTimeout.current) return;
      hoverTimeout.current = setTimeout(() => {
        setIsHovering(true);
        if (onDelayedHoverStart) {
          onDelayedHoverStart(event);
        }
      }, delayMs);
    },
    [delayMs, onDelayedHoverStart],
  );

  const onHoverEnd = useCallback(
    (event: E) => {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
        hoverTimeout.current = null;
      }

      setIsHovering(false);
      if (onDelayedHoverEnd) {
        onDelayedHoverEnd(event);
      }
    },
    [onDelayedHoverEnd],
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
    },
    [],
  );

  return {
    isHovering,
    onHoverStart,
    onHoverEnd,
  };
}
